import { createTRPCReact } from "@trpc/react-query";
import { createTRPCProxyClient, httpBatchLink, TRPCClientError } from "@trpc/client";
import type { AppRouter } from "../../backend/trpc/app-router";
import superjson from "superjson";

export const trpc = createTRPCReact<AppRouter>();

export const getBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    
    // In development (Replit dev environment), use Vite proxy - requests to /api/* will be forwarded to backend
    // In production (Replit app or custom domain), backend serves everything on same domain
    // This works for all environments because:
    // - Dev: Vite proxy forwards /api to localhost:3000
    // - Prod: Backend serves both static files and API on same domain
    return window.location.origin;
  }
  
  return 'http://localhost:3000';
};

export const trpcClient = createTRPCProxyClient<AppRouter>({
  links: [
    httpBatchLink({
      url: `${getBaseUrl()}/api/trpc`,
      transformer: superjson,
      methodOverride: 'POST', // Force POST for all requests (queries + mutations) to avoid URL length limits
      fetch(url, options) {
        return fetch(url, {
          ...options,
          credentials: 'include',
        });
      },
    }),
  ],
});

export const isTRPCClientError = (error: unknown): error is TRPCClientError<AppRouter> => {
  return error instanceof TRPCClientError;
};

export interface ValidationError {
  field: string;
  message: string;
  path: string[];
}

/**
 * Parse Zod validation errors from tRPC error messages
 * Returns structured error information with field names
 */
export const parseValidationErrorsDetailed = (message: string): ValidationError[] => {
  try {
    if (message.startsWith('[') && message.includes('"message"')) {
      const errors = JSON.parse(message);
      if (Array.isArray(errors) && errors.length > 0) {
        return errors.map((err: any) => ({
          field: err.path?.[err.path.length - 1] || 'unknown',
          message: err.message || 'Invalid input',
          path: err.path || [],
        }));
      }
    }
  } catch {
    // If parsing fails, return empty array
  }
  return [];
};

/**
 * Convert field name to human-readable label
 */
const formatFieldName = (field: string): string => {
  if (!field || field === 'unknown') return '';
  return field
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^\w/, c => c.toUpperCase())
    .trim();
};

/**
 * Parse Zod validation errors from tRPC error messages
 * Returns a user-friendly message instead of raw JSON
 */
export const parseValidationErrors = (message: string): string => {
  const errors = parseValidationErrorsDetailed(message);
  if (errors.length > 0) {
    return errors
      .map(err => {
        const fieldLabel = formatFieldName(err.field);
        if (fieldLabel && !err.message.toLowerCase().includes(fieldLabel.toLowerCase())) {
          return `${fieldLabel}: ${err.message}`;
        }
        return err.message;
      })
      .filter((msg, index, self) => self.indexOf(msg) === index)
      .join('. ');
  }
  return message;
};

/**
 * Format any error into a user-friendly message
 * Handles tRPC errors, validation errors, and general errors
 */
export const formatErrorMessage = (error: unknown): string => {
  let message = 'An unexpected error occurred';
  
  if (isTRPCClientError(error)) {
    message = error.message;
  } else if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === 'string') {
    message = error;
  }
  
  // Parse and clean up validation error messages
  return parseValidationErrors(message);
};

export const handleTRPCError = (error: unknown): string => {
  return formatErrorMessage(error);
};

/**
 * Show a user-friendly error alert
 * Use this instead of alert(error.message) to properly format validation errors
 */
export const showFriendlyError = (error: unknown, fallbackMessage?: string): void => {
  const message = formatErrorMessage(error);
  alert(message || fallbackMessage || 'An error occurred. Please try again.');
};

/**
 * Get the first error field from a validation error
 */
export const getFirstErrorField = (error: unknown): string | null => {
  let message = '';
  if (isTRPCClientError(error)) {
    message = error.message;
  } else if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === 'string') {
    message = error;
  }
  
  const errors = parseValidationErrorsDetailed(message);
  if (errors.length > 0 && errors[0].field !== 'unknown') {
    return errors[0].field;
  }
  return null;
};

/**
 * Scroll to the field with an error and highlight it
 * Tries multiple selector strategies to find the element
 */
export const scrollToErrorField = (fieldName: string): boolean => {
  if (!fieldName) return false;
  
  const selectors = [
    `[name="${fieldName}"]`,
    `#${fieldName}`,
    `[data-field="${fieldName}"]`,
    `input[placeholder*="${fieldName}" i]`,
    `textarea[placeholder*="${fieldName}" i]`,
  ];
  
  for (const selector of selectors) {
    try {
      const element = document.querySelector(selector) as HTMLElement;
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
        
        const originalOutline = element.style.outline;
        const originalBoxShadow = element.style.boxShadow;
        element.style.outline = '2px solid #EF4444';
        element.style.boxShadow = '0 0 10px rgba(239, 68, 68, 0.5)';
        
        setTimeout(() => {
          element.style.outline = originalOutline;
          element.style.boxShadow = originalBoxShadow;
        }, 3000);
        
        return true;
      }
    } catch {
      continue;
    }
  }
  return false;
};

/**
 * Show error and scroll to the problematic field
 * Combines alert with field highlighting
 */
export const showErrorAndScrollToField = (error: unknown, fallbackMessage?: string): void => {
  const message = formatErrorMessage(error);
  const fieldName = getFirstErrorField(error);
  
  alert(message || fallbackMessage || 'An error occurred. Please try again.');
  
  if (fieldName) {
    setTimeout(() => {
      scrollToErrorField(fieldName);
    }, 100);
  }
};
